import React, { useRef, useState } from 'react';
import { Label } from '@/components/ui/label';

const CommonFileUpload = React.forwardRef(({
  className,
  label,
  id,
  error,
  required = false,
  accept,
  onFileSelect,
  existingFile = null,
  ...props
}, ref) => {
  const fileInputRef = useRef(null);
  const [fileInfo, setFileInfo] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      let previewUrl = null;
      if (file.type.startsWith('image/')) {
        previewUrl = URL.createObjectURL(file);
      }
      
      const newFileInfo = {
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl: previewUrl
      };
      
      setFileInfo(newFileInfo);
      if (onFileSelect) {
        onFileSelect(file);
      }
    } else {
      setFileInfo(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const event = { target: { files: [files[0]] } };
      handleFileChange(event);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleRemoveFile = () => {
    setFileInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const isImageFile = (fileType) => {
    return fileType.startsWith('image/');
  };

  const isPdfFile = (fileType) => {
    return fileType === 'application/pdf';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      {label && (
        <Label
          htmlFor={id}
          className={`text-sm font-medium mb-1 inline-block transition-colors duration-200 ${
            error ? 'text-destructive' : 'text-foreground'
          }`}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <div className="flex flex-col space-y-2">
        <input
          ref={(el) => {
            fileInputRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          id={id}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          {...props}
        />
        
        <div 
          className={`
            border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer
            ${error ? 'border-destructive bg-destructive/10' : 'border-input hover:border-primary/50'}
            ${fileInfo || (!fileInfo && existingFile) ? 'border-solid' : ''}
          `}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {fileInfo?.previewUrl && (
            <div className="relative">
              <img 
                src={fileInfo.previewUrl} 
                alt="Preview" 
                className="w-full h-40 object-contain bg-muted"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          {fileInfo && !fileInfo.previewUrl && (
            <div className="flex items-center p-4">
              <div className="flex-shrink-0">
                {isPdfFile(fileInfo.type) ? (
                  <div className="bg-red-500 text-white p-3 rounded">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                ) : (
                  <div className="bg-blue-500 text-white p-3 rounded">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{fileInfo.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(fileInfo.size)}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground ml-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          {!fileInfo && existingFile && (
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center min-w-0 flex-1">
                <div className="flex-shrink-0 bg-red-500 text-white p-3 rounded">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {existingFile.description || existingFile.original_filename || existingFile.name || 'Uploaded Document'}
                  </p>
                  <p className="text-xs text-muted-foreground">Click to upload replacement file</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                {(existingFile.file_url || existingFile.url) && (
                  <a
                    href={existingFile.file_url || existingFile.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-medium text-primary hover:underline px-3 py-1.5 rounded border border-border bg-background shadow-xs"
                  >
                    View File
                  </a>
                )}
              </div>
            </div>
          )}
          {!fileInfo && !existingFile && (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-muted-foreground mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            
              <p className="text-xs text-muted-foreground">Supports</p>
              {accept && (
                <p className="text-xs text-muted-foreground mt-1">
                  {accept.split(',').map(format => format.replace('.', '').toUpperCase()).join(', ')}
                </p>
              )}
            </div>
          )}
          {fileInfo?.previewUrl && (
            <div className="p-3 border-t border-input">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{fileInfo.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(fileInfo.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  className="text-xs text-destructive hover:text-destructive/90"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {error && <p className="text-sm text-destructive mt-1 transition-opacity duration-200">{error}</p>}
    </div>
  );
});

CommonFileUpload.displayName = 'CommonFileUpload';

export { CommonFileUpload };